import { useState } from "react";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useCustomPrintoutList } from "../../../hooks/useCustomPrintouts";
import { readStoredDraft, type StoredCustomPrintoutDraft } from "./customPrintoutDraftStorage";
import type { CustomPrintoutSummary } from "./customPrintoutTypes";

/** The first printout in `rows`, or `null` (a new printout) when there are none. */
const firstIdOf = (rows: CustomPrintoutSummary[]): string | null => (rows.length > 0 ? rows[0].id : null);

type OpeningChoice = { printoutId: string | null; startFromId: string | null };

/**
 * What the popup opens on: where the unsaved design kept from before a reload was being made, if that printout (and
 * the one a new printout copies) is still there; else the first printout.
 */
const openingChoiceOf = (rows: CustomPrintoutSummary[], stored: StoredCustomPrintoutDraft | null): OpeningChoice => {
	const exists = (id: string | null): boolean => id === null || rows.some((row) => row.id === id);
	if (stored !== null && exists(stored.printoutId) && exists(stored.startFromId)) return { printoutId: stored.printoutId, startFromId: stored.startFromId };
	return { printoutId: firstIdOf(rows), startFromId: null };
};

/**
 * Which custom printout is shown. Chosen once when the list first answers — where an unsaved design kept from before
 * a reload was being made, else the first printout, or a new one when there are none (or the list couldn't load) —
 * and from then on only by the user. A rename that reorders the
 * list, or another person's changes arriving with a refetch, never moves it.
 *
 * A new printout may start from a saved one (`startFromId`): its preview and first request use that printout's current
 * template, and saving still creates a new printout.
 */
export const useCustomPrintoutSelection = () => {
	const listQuery = useCustomPrintoutList();
	const { chosenDepartment } = useHospitalData();
	const departmentId = chosenDepartment.department_id;
	// DEFAULT: no rows until the list has answered; the picker shows its own loading and error states meanwhile.
	const printouts = listQuery.data === undefined ? [] : listQuery.data;
	/** `undefined` until the list first answers; then the shown printout's id, or `null` for a new one. */
	const [chosenId, setChosenId] = useState<string | null | undefined>(undefined);
	/** The saved printout a new one starts from; `null` = the reference sheet, or a saved printout is shown. */
	const [startFromId, setStartFromId] = useState<string | null>(null);
	if (chosenId === undefined && !listQuery.isPending) {
		const opening = listQuery.isSuccess ? openingChoiceOf(listQuery.data, readStoredDraft(departmentId)) : { printoutId: null, startFromId: null };
		setChosenId(opening.printoutId);
		setStartFromId(opening.startFromId);
	}

	return {
		listQuery,
		printouts,
		/** `null` = a new printout (and, until the list answers, nothing chosen yet — see `isResolving`). */
		printoutId: chosenId === undefined ? null : chosenId,
		isResolving: chosenId === undefined,
		startFromId,
		select: (id: string | null): void => {
			setChosenId(id);
			setStartFromId(null);
		},
		/** A new printout that starts from the saved printout `id`. */
		startFrom: (id: string): void => {
			setChosenId(null);
			setStartFromId(id);
		},
		/** The printout to show once `deletedId` is gone: the first remaining one, or a new one. */
		nextAfterDelete: (deletedId: string): string | null => firstIdOf(printouts.filter((printout) => printout.id !== deletedId)),
	};
};
